import { useState, useCallback, useRef } from 'react'
import {
  startAuthentication,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { Check, Loader2, Shield, X } from 'lucide-react'
import { apiFetch, apiJson } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/useAuth'
import { fingerprint } from '@/lib/utils'
import type { Credential } from '@/lib/auth/types'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Eyebrow } from '@/components/hawk-primitives'

type StepStatus = 'idle' | 'running' | 'done' | 'error'

interface Step {
  label: string
  status: StepStatus
  detail?: string
  durationMs?: number
}

export function StepUpTest({ credentials }: { credentials: Credential[] }) {
  const { stepUpPolicy } = useAuth()
  const scopes = Object.keys(stepUpPolicy)
  const [selectedScope, setSelectedScope] = useState(scopes[0] ?? '')
  const [selectedKeyId, setSelectedKeyId] = useState<string>('all')
  const [steps, setSteps] = useState<Step[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const startTimeRef = useRef(0)

  const scopeEnforced = stepUpPolicy[selectedScope] === true

  const updateStep = useCallback((index: number, update: Partial<Step>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...update } : s)))
  }, [])

  const timed = useCallback(<T,>(fn: () => Promise<T>) => {
    startTimeRef.current = performance.now()
    return fn().then((result) => ({
      result,
      durationMs: Math.round(performance.now() - startTimeRef.current),
    }))
  }, [])

  const runTest = useCallback(async () => {
    setIsRunning(true)
    setSteps([
      { label: 'Clear existing step-up', status: 'idle' },
      { label: 'Verify enforcement', status: 'idle' },
      { label: 'Request challenge', status: 'idle' },
      { label: 'Authenticate with key', status: 'idle' },
      { label: 'Submit assertion', status: 'idle' },
      { label: 'Confirm access granted', status: 'idle' },
    ])

    const totalStart = performance.now()

    try {
      updateStep(0, { status: 'running', detail: 'Invalidating existing step-up session...' })
      const { durationMs: clearDuration } = await timed(() =>
        apiFetch('/auth/step-up', { method: 'DELETE' }),
      )
      updateStep(0, {
        status: 'done',
        durationMs: clearDuration,
        detail: 'Step-up session cleared',
      })

      updateStep(1, {
        status: 'running',
        detail: `Calling verify without step-up (scope: ${selectedScope})...`,
      })
      const { result: preResponse, durationMs: preDuration } = await timed(() =>
        apiFetch('/auth/step-up/verify', {
          method: 'POST',
          body: JSON.stringify({ scope: selectedScope }),
        }),
      )

      if (scopeEnforced) {
        if (preResponse.status === 403) {
          const body = await preResponse.json().catch(() => ({}))
          if (body.code === 'STEP_UP_REQUIRED') {
            updateStep(1, {
              status: 'done',
              durationMs: preDuration,
              detail: 'Correctly blocked - server returned STEP_UP_REQUIRED',
            })
          } else {
            updateStep(1, {
              status: 'error',
              durationMs: preDuration,
              detail: `Got 403 but wrong error code: ${body.code ?? 'none'}`,
            })
            return
          }
        } else if (preResponse.ok) {
          updateStep(1, {
            status: 'error',
            durationMs: preDuration,
            detail: 'FAIL - server allowed access WITHOUT step-up',
          })
          return
        } else {
          updateStep(1, {
            status: 'error',
            durationMs: preDuration,
            detail: `Unexpected status ${preResponse.status}`,
          })
          return
        }
      } else {
        if (preResponse.ok) {
          updateStep(1, {
            status: 'done',
            durationMs: preDuration,
            detail: `Scope "${selectedScope}" is not enforced - server allowed (expected)`,
          })
          updateStep(2, { status: 'done', detail: 'Skipped - not enforced' })
          updateStep(3, { status: 'done', detail: 'Skipped - not enforced' })
          updateStep(4, { status: 'done', detail: 'Skipped - not enforced' })
          updateStep(5, { status: 'done', detail: 'Skipped - not enforced' })
          return
        }
        updateStep(1, {
          status: 'error',
          durationMs: preDuration,
          detail: `Scope not enforced but server returned ${preResponse.status}`,
        })
        return
      }

      updateStep(2, { status: 'running' })
      const { result: beginResponse, durationMs: beginDuration } = await timed(() =>
        apiJson<{ publicKey: PublicKeyCredentialRequestOptionsJSON }>(
          '/auth/step-up/begin',
          { method: 'POST' },
        ),
      )
      const { publicKey } = beginResponse
      const credCount = publicKey.allowCredentials?.length ?? 0

      updateStep(2, {
        status: 'done',
        durationMs: beginDuration,
        detail: `Challenge received (${credCount} credential${credCount !== 1 ? 's' : ''})`,
      })

      const testOptions = { ...publicKey }
      if (selectedKeyId !== 'all' && testOptions.allowCredentials) {
        const selectedCred = credentials.find((c) => c.id === selectedKeyId)
        if (selectedCred) {
          testOptions.allowCredentials = testOptions.allowCredentials.filter(
            (ac) => ac.id === selectedCred.credential_id,
          )
        }
      }
      const selectedKeyName =
        selectedKeyId === 'all'
          ? 'any key'
          : credentials.find((c) => c.id === selectedKeyId)?.name ?? 'selected key'
      updateStep(3, { status: 'running', detail: `Waiting for ${selectedKeyName}...` })
      const { result: assertion, durationMs: authDuration } = await timed(() =>
        startAuthentication({ optionsJSON: testOptions }),
      )

      updateStep(3, {
        status: 'done',
        durationMs: authDuration,
        detail: 'Assertion signed by authenticator',
      })

      updateStep(4, { status: 'running' })
      const { result: completeResponse, durationMs: completeDuration } = await timed(() =>
        apiFetch('/auth/step-up/complete', {
          method: 'POST',
          body: JSON.stringify(assertion),
        }),
      )

      if (!completeResponse.ok) {
        updateStep(4, {
          status: 'error',
          durationMs: completeDuration,
          detail: (await completeResponse.text()) || 'Server rejected assertion',
        })
        return
      }

      updateStep(4, {
        status: 'done',
        durationMs: completeDuration,
        detail: 'Step-up session established',
      })

      updateStep(5, { status: 'running', detail: 'Calling verify with active step-up...' })
      const { result: postResponse, durationMs: postDuration } = await timed(() =>
        apiFetch('/auth/step-up/verify', {
          method: 'POST',
          body: JSON.stringify({ scope: selectedScope }),
        }),
      )

      if (postResponse.ok) {
        const body = await postResponse.json().catch(() => ({}))
        const totalDuration = Math.round(performance.now() - totalStart)
        updateStep(5, {
          status: 'done',
          durationMs: postDuration,
          detail: `Access granted (expires in ${body.expiresIn}s) - total ${totalDuration}ms`,
        })
      } else {
        updateStep(5, {
          status: 'error',
          durationMs: postDuration,
          detail: `FAIL - server rejected even WITH step-up (status ${postResponse.status})`,
        })
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error'
      setSteps((prev) => {
        const idx = prev.findIndex((s) => s.status === 'running')
        if (idx < 0) return prev
        return prev.map((s, i) =>
          i === idx
            ? {
                ...s,
                status: 'error' as const,
                detail: msg,
                durationMs: Math.round(performance.now() - startTimeRef.current),
              }
            : s,
        )
      })
    } finally {
      setIsRunning(false)
    }
  }, [updateStep, timed, scopeEnforced, selectedScope, selectedKeyId, credentials])

  return (
    <section>
      <div className="mb-4">
        <Eyebrow>Test your key</Eyebrow>
      </div>
      <div className="border border-border bg-card rounded-md p-6 space-y-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Runs the full step-up flow against a selected scope and validates server-side
          enforcement end to end.
        </p>
        {scopes.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Scope
              </label>
              <Select
                value={selectedScope}
                onValueChange={(v) => {
                  setSelectedScope(v)
                  setSteps([])
                }}
                disabled={isRunning}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scopes.map((scope) => (
                    <SelectItem key={scope} value={scope}>
                      {scope} {stepUpPolicy[scope] ? '(enforced)' : '(not enforced)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Key
              </label>
              <Select
                value={selectedKeyId}
                onValueChange={(v) => {
                  setSelectedKeyId(v)
                  setSteps([])
                }}
                disabled={isRunning}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All keys</SelectItem>
                  {credentials.map((cred) => (
                    <SelectItem key={cred.id} value={cred.id}>
                      {cred.name ?? 'Security Key'} ({fingerprint(cred.credential_id)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <p className="text-sm text-destructive">
            No scopes configured. Set HAWK_STEP_UP_POLICY to test enforcement.
          </p>
        )}

        {steps.length > 0 && (
          <div className="border border-border rounded-md divide-y divide-border">
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-3 p-3">
                <div className="mt-0.5">
                  {step.status === 'idle' && (
                    <span className="block size-3 rounded-full border border-border" />
                  )}
                  {step.status === 'running' && (
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  )}
                  {step.status === 'done' && (
                    <Check className="size-4 text-[var(--color-chart-2)]" />
                  )}
                  {step.status === 'error' && (
                    <X className="size-4 text-destructive" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{step.label}</div>
                  {step.detail && (
                    <div className="text-xs text-muted-foreground mt-0.5">{step.detail}</div>
                  )}
                </div>
                {step.durationMs !== undefined && (
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {step.durationMs}ms
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <Button onClick={runTest} disabled={isRunning || !selectedScope} variant="outline">
          {isRunning ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Shield className="size-4" />
              {steps.length > 0 ? 'Run again' : 'Run test'}
            </>
          )}
        </Button>
      </div>
    </section>
  )
}
